// servizi/scelte.ts — v040: «Le tue scelte», date UNA volta e poi cambiabili nel profilo.
// v045: si chiedono DOPO la primissima partita (prima si chiedevano prima di giocare, insieme alla spiegazione del
// volto: troppe cose insieme), con «Sì a tutto», «Scelgo io» o «Non ora». Le voci: foto della partita, video delle
// risate e «Video per noi» (se dite sì tutti e due, il video della risata ce lo lasciate per i nostri social e il
// sito; solo maggiorenni). «Video online» non è più una scelta: il link che si manda porta sempre alla sfida.
// Chi ha detto no a qualcosa se lo vede riproporre (solo quello) a fine partita, una volta per apertura dell'app e
// non più di una volta ogni 20 ore.
// Sul telefono stanno nelle preferenze; con l'account anche sull'account (uguali su tutti i telefoni).
import { useSyncExternalStore } from "react";
import type { Scelte } from "../../../condiviso/scelte.ts";
import { leggiPreferenza, salvaPreferenza } from "./preferenze.ts";
import { richiesta } from "./api.ts";
import { idDispositivo } from "./dispositivo.ts";
import { salvaScelteAccount, statoAccount } from "./account.ts";
import { PREFERENZE, IMP_APP } from "../costanti.ts";
import { stanza } from "../gioco/stanza.ts";
import { stanzaGruppo } from "../gioco/stanzaGruppo.ts";
import { eBooleano, leggiImpostazione } from "./impostazioni.ts";
import { maggiorenneQui } from "./video.ts";

export type { Scelte };
export type VoceScelta = keyof Scelte;

export function scelteAttuali(): Scelte {
  const foto = leggiPreferenza(PREFERENZE.foto);
  return {
    // prima della v040 non c'era la domanda sulle foto: vale quella sul video
    foto: foto ? foto === "si" : leggiPreferenza(PREFERENZE.clip) === "si",
    video: leggiPreferenza(PREFERENZE.clip) === "si",
    noi: leggiPreferenza(PREFERENZE.noi) === "si",
  };
}

/** Le scelte sono già state fatte (su questo telefono o con l'account). */
export function scelteFatte(): boolean {
  const s = statoAccount();
  if (s.tipo === "registrato" && s.io.scelte) return true;
  // prima della v040 la domanda sul video era già stata fatta (alla prima partita): vale come scelta fatta
  return leggiPreferenza(PREFERENZE.scelteFatte) === "1" || leggiPreferenza(PREFERENZE.clip) === "si" || leggiPreferenza(PREFERENZE.clip) === "no";
}

/** «Video per noi» si può chiedere qui (impostazione accesa, maggiorenne). */
export function noiPossibileQui(): boolean {
  const s = statoAccount();
  // con l'account lo dice il server (18 anni compiuti), anche su un telefono dove non si è scritta la data di nascita
  const maggiorenne = maggiorenneQui() || (s.tipo === "registrato" && s.io.puoAcquistoSito === true);
  return leggiImpostazione(IMP_APP.videoNoi, true, eBooleano) && maggiorenne;
}

const ascoltatori = new Set<() => void>();
let istantanea = JSON.stringify(scelteAttuali());
let oggetto: Scelte = scelteAttuali();
function leggi(): Scelte {
  const ora = JSON.stringify(scelteAttuali());
  if (ora !== istantanea) {
    istantanea = ora;
    oggetto = JSON.parse(ora) as Scelte;
  }
  return oggetto;
}

/**
 * Ritiro del «Video per noi»: i video in cui si vede questo telefono (o questo account) si tolgono subito dal nostro
 * archivio e chi gestisce i social riceve la richiesta di toglierli entro 24 ore. Restituisce quanti video erano.
 */
export async function ritiraVideoNoi(): Promise<number> {
  const r = await richiesta<{ video: number }>("/api/v1/io/revoca-video-noi", { metodo: "POST", corpo: { dispositivo: idDispositivo() } });
  return r.video;
}

/** Salva le scelte e le applica subito anche alla partita in corso (se ce n'è una): valgono dal round dopo. */
export async function salvaScelte(sc: Scelte): Promise<void> {
  const prima = scelteAttuali();
  // «Video per noi» senza video delle risate non ha senso (e senza la maggiore età non si chiede)
  const nuove: Scelte = { ...sc, noi: sc.noi && sc.video && noiPossibileQui() };
  decisaInQuestaApertura = true;
  salvaPreferenza(PREFERENZE.promemoriaScelte, String(Date.now()));
  const p = salvaScelteAccount(nuove);
  ascoltatori.forEach((a) => a());
  stanza.consensoClip(nuove.video);
  stanza.consensoFoto(nuove.foto);
  stanza.consensoNoi(nuove.noi);
  // v042: anche nella partita a 4
  stanzaGruppo.consenso("clip", nuove.video);
  stanzaGruppo.consenso("foto", nuove.foto);
  // chi aveva detto sì al «Video per noi» e ora dice no: si ritira anche per i video già fatti
  if (prima.noi && !nuove.noi) void ritiraVideoNoi().catch(() => undefined);
  await p;
}

export function useScelte(): Scelte {
  return useSyncExternalStore(
    (f) => {
      ascoltatori.add(f);
      return () => ascoltatori.delete(f);
    },
    leggi,
  );
}

// --- v045: la domanda dopo la prima partita e il promemoria ----------------------------------------------------

/** Le voci che hanno senso su questo telefono (video spenti o telefono che non li sa fare: niente voce). */
export function vociPossibili(videoPossibile: boolean): VoceScelta[] {
  const v: VoceScelta[] = ["foto"];
  if (videoPossibile) v.push("video");
  if (videoPossibile && noiPossibileQui()) v.push("noi");
  return v;
}

/** Le voci a cui si è detto no (tra quelle possibili): sono quelle che il promemoria ripropone. */
export function vociRifiutate(videoPossibile: boolean): VoceScelta[] {
  const sc = scelteAttuali();
  return vociPossibili(videoPossibile).filter((k) => !sc[k]);
}

// In questa apertura dell'app: si è già deciso (niente promemoria subito dopo) / il promemoria si è già visto.
let decisaInQuestaApertura = false;
let promemoriaVisto = false;
/** Il promemoria torna al massimo una volta per apertura dell'app e non prima di 20 ore dall'ultima volta. */
const PROMEMORIA_OGNI_MS = 20 * 3600_000;

/** Cosa chiedere a fine partita: la domanda della prima volta, il promemoria (una volta per apertura) o niente. */
export function domandaDaFare(videoPossibile: boolean, ora = Date.now()): "prima" | "promemoria" | null {
  if (!scelteFatte()) return "prima";
  if (decisaInQuestaApertura || promemoriaVisto) return null;
  const ultimo = Number(leggiPreferenza(PREFERENZE.promemoriaScelte) ?? "0");
  if (Number.isFinite(ultimo) && ora - ultimo < PROMEMORIA_OGNI_MS) return null;
  return vociRifiutate(videoPossibile).length > 0 ? "promemoria" : null;
}
export function segnaPromemoriaVisto(): void {
  promemoriaVisto = true;
  salvaPreferenza(PREFERENZE.promemoriaScelte, String(Date.now()));
}
